import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SearchIcon from "@mui/icons-material/Search";
import { useUserContext } from "../hooks/UserContext";
import { LineaVenta } from "../models/LineaVentaModel";
import { Producto } from "../models/ProductoModel";
import { fetchProductoComandaMesaImagen } from "../services/productoService";
import { isAbortError } from "../services/fetchService";
import { CurrencyFormatter } from "../utils/CurrencyFormatter";

const imagenProductoCache = new Map<number, string | undefined>();

const valorProducto = (producto: Producto, mayuscula: keyof Producto, minuscula: keyof Producto) =>
  producto[mayuscula] ?? producto[minuscula];

const idProducto = (producto: Producto) => Number(valorProducto(producto, "IDPRODUCTO", "idProducto") ?? 0);
const codigoProducto = (producto: Producto) => String(valorProducto(producto, "CODIGO", "codigo") ?? "");
const nombreProducto = (producto: Producto) => String(valorProducto(producto, "PRODUCTO", "producto") ?? "");
const precioProducto = (producto: Producto) => Number(valorProducto(producto, "CONTADO", "precio") ?? 0);
const idRubroProducto = (producto: Producto) => String(valorProducto(producto, "IDRUBRO", "idRubro") ?? "");
const rubroProducto = (producto: Producto) => String(valorProducto(producto, "RUBRO", "rubro") ?? "Sin rubro");
const idSubrubroProducto = (producto: Producto) => String(valorProducto(producto, "IDSUBRUBRO", "idSubrubro") ?? "");
const subrubroProducto = (producto: Producto) => String(valorProducto(producto, "SUBRUBRO", "subrubro") ?? "");

const ImagenProducto = ({ producto, user }: { producto: Producto; user: any }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const id = idProducto(producto);
  const [visible, setVisible] = useState(false);
  const [imagen, setImagen] = useState<string | undefined>(() => imagenProductoCache.get(id));

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "0px" });
    observer.observe(elemento);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !user || id <= 0 || imagenProductoCache.has(id)) {
      setImagen(imagenProductoCache.get(id));
      return;
    }
    const controller = new AbortController();
    fetchProductoComandaMesaImagen(user, id, controller.signal)
      .then((url) => {
        imagenProductoCache.set(id, url);
        setImagen(url);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        imagenProductoCache.set(id, undefined);
      });
    return () => controller.abort();
  }, [id, user, visible]);

  return (
    <Box ref={ref} sx={{ height: 130, bgcolor: "grey.100", display: "grid", placeItems: "center" }}>
      {imagen ? (
        <CardMedia component="img" image={imagen} alt={nombreProducto(producto)} sx={{ width: "100%", height: 130, objectFit: "cover" }} />
      ) : (
        <RestaurantIcon sx={{ fontSize: 44, color: "text.disabled" }} />
      )}
    </Box>
  );
};

type SelectorProductosVentaProps = {
  open: boolean;
  productos: Producto[];
  lineas: LineaVenta[];
  onAgregar: (producto: Producto) => void;
  onClose: () => void;
  destino?: string;
  productosPorPagina?: number;
};

const SelectorProductosVenta = ({
  open,
  productos,
  lineas,
  onAgregar,
  onClose,
  destino = "venta",
  productosPorPagina,
}: SelectorProductosVentaProps) => {
  const { getUser, user } = useUserContext();
  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [busqueda, setBusqueda] = useState("");
  const [rubroSeleccionado, setRubroSeleccionado] = useState("");
  const [subrubroSeleccionado, setSubrubroSeleccionado] = useState("");
  const [pagina, setPagina] = useState(0);

  useEffect(() => {
    if (!open) {
      setBusqueda("");
      setRubroSeleccionado("");
      setSubrubroSeleccionado("");
      setPagina(0);
    }
  }, [open]);

  const rubros = useMemo(() => Array.from(new Map(productos.map((producto) => [
    idRubroProducto(producto) || `nombre:${rubroProducto(producto)}`,
    { id: idRubroProducto(producto) || `nombre:${rubroProducto(producto)}`, nombre: rubroProducto(producto) },
  ])).values()).sort((a, b) => a.nombre.localeCompare(b.nombre)), [productos]);

  const subrubros = useMemo(() => Array.from(new Map(productos
    .filter((producto) => !rubroSeleccionado || (idRubroProducto(producto) || `nombre:${rubroProducto(producto)}`) === rubroSeleccionado)
    .filter((producto) => idSubrubroProducto(producto) || subrubroProducto(producto))
    .map((producto) => [
      idSubrubroProducto(producto) || `nombre:${subrubroProducto(producto)}`,
      { id: idSubrubroProducto(producto) || `nombre:${subrubroProducto(producto)}`, nombre: subrubroProducto(producto) },
    ])).values()).sort((a, b) => a.nombre.localeCompare(b.nombre)), [productos, rubroSeleccionado]);

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLocaleLowerCase();
    return productos.filter((producto) => {
      const rubroId = idRubroProducto(producto) || `nombre:${rubroProducto(producto)}`;
      const subrubroId = idSubrubroProducto(producto) || `nombre:${subrubroProducto(producto)}`;
      return (!texto || nombreProducto(producto).toLocaleLowerCase().includes(texto) || codigoProducto(producto).toLocaleLowerCase().includes(texto))
        && (!rubroSeleccionado || rubroId === rubroSeleccionado)
        && (!subrubroSeleccionado || subrubroId === subrubroSeleccionado);
    });
  }, [busqueda, productos, rubroSeleccionado, subrubroSeleccionado]);

  const paginasTotales = productosPorPagina
    ? Math.ceil(filtrados.length / productosPorPagina)
    : 1;
  const paginaActual = productosPorPagina && paginasTotales > 0
    ? Math.min(pagina, paginasTotales - 1)
    : 0;
  const productosVisibles = productosPorPagina
    ? filtrados.slice(paginaActual * productosPorPagina, (paginaActual + 1) * productosPorPagina)
    : filtrados;

  useEffect(() => {
    if (pagina !== paginaActual) {
      setPagina(paginaActual);
    }
  }, [pagina, paginaActual]);

  const handleBusquedaChange = (value: string) => {
    setBusqueda(value);
    setPagina(0);
  };

  const handleRubroChange = (idRubro: string) => {
    setRubroSeleccionado(idRubro);
    setSubrubroSeleccionado("");
    setPagina(0);
  };

  const handleSubrubroChange = (idSubrubro: string) => {
    setSubrubroSeleccionado(idSubrubro);
    setPagina(0);
  };

  const cantidadAgregada = (producto: Producto) => lineas
    .filter((linea) => idProducto(linea.producto) === idProducto(producto))
    .reduce((total, linea) => total + linea.cantidad, 0);

  return (
    <Dialog open={open} fullWidth maxWidth="md" fullScreen={fullScreen} onClose={onClose}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column", minHeight: { xs: "100%", sm: 560 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>Agregar productos</Typography>
          <Chip color="primary" label={`${lineas.reduce((total, linea) => total + linea.cantidad, 0)} en ${destino}`} />
        </Box>
        <TextField
          autoFocus
          fullWidth
          value={busqueda}
          onChange={(event) => handleBusquedaChange(event.target.value)}
          placeholder="Buscar por nombre o código"
          sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <Typography variant="caption" color="text.secondary" fontWeight={700}>Rubro</Typography>
        <Box role="group" aria-label="Filtrar por rubro" sx={{ display: "flex", gap: 1, overflowX: "auto", py: 1, WebkitOverflowScrolling: "touch" }}>
          <Chip label="Todos" color={!rubroSeleccionado ? "primary" : "default"} variant={!rubroSeleccionado ? "filled" : "outlined"} onClick={() => handleRubroChange("")} sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }} />
          {rubros.map((rubro) => <Chip key={rubro.id} label={rubro.nombre} color={rubroSeleccionado === rubro.id ? "primary" : "default"} variant={rubroSeleccionado === rubro.id ? "filled" : "outlined"} onClick={() => handleRubroChange(rubroSeleccionado === rubro.id ? "" : rubro.id)} sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }} />)}
        </Box>
        {subrubros.length > 0 && <>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Subrubro</Typography>
          <Box role="group" aria-label="Filtrar por subrubro" sx={{ display: "flex", gap: 1, overflowX: "auto", py: 1, WebkitOverflowScrolling: "touch" }}>
            <Chip label="Todos" color={!subrubroSeleccionado ? "secondary" : "default"} variant={!subrubroSeleccionado ? "filled" : "outlined"} onClick={() => handleSubrubroChange("")} sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }} />
            {subrubros.map((subrubro) => <Chip key={subrubro.id} label={subrubro.nombre} color={subrubroSeleccionado === subrubro.id ? "secondary" : "default"} variant={subrubroSeleccionado === subrubro.id ? "filled" : "outlined"} onClick={() => handleSubrubroChange(subrubroSeleccionado === subrubro.id ? "" : subrubro.id)} sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }} />)}
          </Box>
        </>}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
          Busque por nombre o código, filtre con los tags y toque Agregar tantas veces como necesite. Use las páginas para recorrer los resultados. El catálogo queda abierto hasta presionar Listo.
        </Typography>
        <Box sx={{ flex: 1, overflowY: "auto", pr: { sm: 0.5 } }}>
          <Grid container spacing={1.5}>
            {productosVisibles.map((producto) => <Grid item xs={12} sm={6} md={4} key={idProducto(producto) || codigoProducto(producto)}>
              <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <ImagenProducto producto={producto} user={userLoggedIn} />
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">{codigoProducto(producto)}</Typography>
                  <Typography fontWeight={700}>{nombreProducto(producto)}</Typography>
                  {(rubroProducto(producto) || subrubroProducto(producto)) && <Typography variant="caption" color="text.secondary">{[rubroProducto(producto), subrubroProducto(producto)].filter(Boolean).join(" · ")}</Typography>}
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>{CurrencyFormatter.format(precioProducto(producto))}</Typography>
                </CardContent>
                <CardActions>
                  <Badge badgeContent={cantidadAgregada(producto)} color="secondary" sx={{ width: "100%" }}>
                    <Button fullWidth variant="contained" startIcon={<AddShoppingCartIcon />} onClick={() => onAgregar(producto)}>Agregar</Button>
                  </Badge>
                </CardActions>
              </Card>
            </Grid>)}
          </Grid>
          {filtrados.length === 0 && <Alert severity="info">No se encontraron productos para la búsqueda y los filtros seleccionados.</Alert>}
        </Box>
        {productosPorPagina && paginasTotales > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Pagination
              count={paginasTotales}
              page={paginaActual + 1}
              onChange={(_event, nuevaPagina) => setPagina(nuevaPagina - 1)}
              color="primary"
              aria-label="Paginación de productos"
              getItemAriaLabel={(type, page) => {
                if (type === "page") {
                  return `Ir a página ${page}`;
                }
                if (type === "first") {
                  return "Ir a la primera página";
                }
                if (type === "last") {
                  return "Ir a la última página";
                }
                if (type === "next") {
                  return "Ir a la página siguiente";
                }
                return "Ir a la página anterior";
              }}
            />
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button variant="contained" color="success" onClick={onClose}>Listo, volver a {destino}</Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SelectorProductosVenta;
